import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SidePanel from "../ui/SidePanel";
import { Icon, D, VehiclePhoto } from "./icons";
import { errText } from "./quick";
import { sendToTrucks } from "../../utils/matchingApi";
import { fetchAllOrders, notExpired, money, fmtDateShort, nf, ORDER_TYPES, shortId } from "../loads/common";

/**
 * «Отправить заявку» on a truck: instead of typing a route, the shipper picks one of their own
 * active loads from a list («Показать список ордеров → Выбрать из списка») and the truck gets it
 * as a request (POST /m/orders/:id/send with vehicle_ids — trucks the engine did not pair are
 * paired by hand). `trucks` = one or several trucks.
 */
export default function TruckRequestPanel({ trucks, onClose, onSent }) {
  const user = useSelector((s) => s.auth?.user);
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const first = trucks[0];
  const free = trucks.filter((t) => t.status !== "BUSY");

  useEffect(() => {
    let alive = true;
    fetchAllOrders()
      .then((d) => {
        if (!alive) return;
        const mine = d.filter((o) => o.owner === user?.id && o.status === 1 && notExpired(o));
        setOrders(mine);
        if (mine.length === 1) setPick(mine[0].uuid);
      })
      .catch(() => alive && setOrders([]));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  const submit = async () => {
    if (!pick) return setError("Выберите груз из списка");
    setBusy(true);
    setError("");
    try {
      const r = await sendToTrucks(pick, { vehicle_ids: free.map((t) => Number(t.vehicle_id)) });
      if (!r?.sent) throw new Error("Заявку не удалось отправить: машина сейчас недоступна.");
      onSent(free.map((t) => Number(t.vehicle_id)), { order: pick });
      onClose();
    } catch (e) {
      setError(errText(e, "Не удалось отправить заявку"));
      setBusy(false);
    }
  };

  const title = trucks.length === 1 ? `Заявка — ${first.driver_name || first.mark || "машина"}` : `Заявка — машин: ${trucks.length}`;
  return (
    <SidePanel
      title={title}
      subtitle="Выберите ордер"
      className="details"
      onClose={onClose}
      footer={
        <>
          <button className="button primary" disabled={busy || !pick || !free.length} onClick={submit} type="button">
            {busy ? "Отправляем…" : "Отправить заявку"}
          </button>
          <button className="button" onClick={onClose} type="button">
            Отмена
          </button>
        </>
      }
    >
      {trucks.length === 1 ? (
        <div className="dt-vehicle">
          <VehiclePhoto label={first.mark || "Машина"} category={first.category_name} />
          <div>
            <h3>{first.mark || "Машина"}</h3>
            <p>{[first.body_type_name, first.max_load_weight != null ? `${nf(first.max_load_weight)} т` : null, first.max_load_volume != null ? `${nf(first.max_load_volume)} м³` : null, first.city].filter(Boolean).join(" • ")}</p>
          </div>
        </div>
      ) : null}
      <p className="side-note">Выберите груз, который хотите предложить перевозчику.</p>
      {orders && orders.length > 1 ? (
        <label className="side-field">
          Поиск ордера
          <input onChange={(e) => setQ(e.target.value)} placeholder="Номер, груз или маршрут" value={q} />
        </label>
      ) : null}
      {!free.length ? <p className="side-error">Машина сейчас в рейсе — заявку отправить нельзя.</p> : null}
      {orders === null ? <p className="side-note">Загружаем ваши грузы…</p> : null}
      {orders && orders.length === 0 ? (
        <div className="side-list">
          <p className="side-note">У вас нет активных грузов. Опубликуйте груз, и его можно будет отправить перевозчику.</p>
          <button className="button primary" onClick={() => navigate("/order")} type="button">
            Опубликовать груз
          </button>
        </div>
      ) : null}
      <div className="side-list" role="radiogroup" aria-label="Ваши активные грузы">
        {(orders || []).filter((o) => !q.trim() || [shortId(o), ORDER_TYPES[o.item?.type], o.from_address, o.to_address].join(" ").toLowerCase().includes(q.trim().toLowerCase())).map((o) => {
          const w = o.item?.weight != null ? `${nf(o.item.weight)} т` : null;
          const on = pick === o.uuid;
          return (
            <button aria-checked={on} className={`side-card pick${on ? " on" : ""}`} key={o.uuid} onClick={() => setPick(o.uuid)} role="radio" type="button">
              <span className="badge-icon">
                <Icon d={D.box} />
              </span>
              <span className="grow">
                <strong>
                  {o.from_address || "—"} → {o.to_address || "—"}
                </strong>
                <small>
                  {[`#${shortId(o)}`, ORDER_TYPES[o.item?.type], w, fmtDateShort(o.from_date), money(o.price, o.currency)].filter((x) => x && x !== "—").join(" · ")}
                </small>
              </span>
              {on ? <Icon d={D.check} /> : null}
            </button>
          );
        })}
      </div>
      {error ? (
        <p className="side-error" role="alert">
          {error}
        </p>
      ) : null}
    </SidePanel>
  );
}
